import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setExpiresAt(new Date(Date.now() + 60 * 60 * 1000));
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-5 py-16" data-testid="forgot-password-page">
      <div className="bg-card border border-brand-line rounded-2xl p-8">
        <div className="w-12 h-12 rounded-xl bg-brand-emerald/10 flex items-center justify-center mb-5">
          <KeyRound className="w-6 h-6 text-brand-emerald" />
        </div>
        <h1 className="font-heading font-bold text-2xl text-brand-ink">Forgot your password?</h1>
        <p className="text-brand-muted text-sm mt-1 mb-6">Enter your registered email and we'll send you a link to reset it.</p>

        {sent ? (
          <div className="rounded-xl bg-brand-emerald/5 border border-brand-emerald/20 p-4 flex items-start gap-3" data-testid="forgot-password-sent">
            <MailCheck className="w-5 h-5 text-brand-emerald shrink-0 mt-0.5" />
            <div className="text-sm text-brand-ink">
              If an account exists for <span className="font-semibold">{email}</span>, a password reset link is on its way.
              <div className="mt-2 text-brand-muted" data-testid="forgot-password-expiry">
                The link expires at <span className="font-semibold text-brand-ink">{expiresAt?.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span> (about 1 hour from now). Request a new one if it lapses.
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-password-email" className="mt-1.5" />
            </div>
            <Button type="submit" disabled={loading} data-testid="forgot-password-submit" className="w-full rounded-full bg-brand-emerald hover:bg-brand-emeraldHover h-11">
              {loading ? "Sending..." : "Send reset link"}
            </Button>
          </form>
        )}

        <div className="mt-6 pt-5 border-t border-brand-line text-center">
          <Link to="/login" data-testid="forgot-back-to-login" className="text-sm text-brand-emerald font-semibold hover:underline">← Back to login</Link>
        </div>
      </div>
    </div>
  );
}
